import { FC, useId, useRef } from "react";
import { Stack } from "@mui/material";
import { FormTypography } from "@/forms-design-system/FormTypography";

import { useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormTextField } from "@/forms-design-system/FormTextField";
import { Close } from "@/icons/filled";
import { Search } from "@/icons/outlined";

interface ProviderSearchProps {
  resultsId: string;
  isOpen: boolean;
  onFocus: () => void;
  value: string;
  onChange: (value: string) => void;
}

export const ProviderSearch: FC<ProviderSearchProps> = ({ value, onChange, resultsId, isOpen, onFocus }) => {
  const { t } = useTranslate();
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const clear = () => {
    onChange("");
    inputRef.current?.focus();
  };

  return (
    <Stack gap={0.5}>
      <FormTypography id={id} variant="body2" vibe="moderate">
        {t("common", "daf.selectProvider")}
      </FormTypography>
      <FormTextField
        inputProps={{
          "aria-labelledby": id,
          role: "combobox",
          "aria-expanded": isOpen,
          "aria-controls": resultsId,
          "aria-autocomplete": "list",
        }}
        onFocus={onFocus}
        inputRef={inputRef}
        value={value}
        placeholder={t("common", "daf.searchProviders")}
        onChange={(event) => onChange(event.target.value)}
        startIcon={<Search />}
        endAdornment={
          value ? (
            <FormButton
              variant="ghost"
              size="small"
              iconButton
              startIcon={<Close />}
              onClick={clear}
              aria-label={t("common", "clear")}
            />
          ) : undefined
        }
        components={{
          input: {
            sx: (theme) => ({
              ...theme.typography.body1,
              minHeight: theme.spacing(6),
              "& .MuiInputBase-input": {
                minHeight: 0,
                height: theme.spacing(3),
                padding: theme.spacing(1.375, 1),
                "&::placeholder": { opacity: 1 },
              },
            }),
          },
          startIcon: {
            sx: (theme) => ({
              width: theme.spacing(2),
              height: theme.spacing(2),
              marginInlineStart: theme.spacing(1.375),
            }),
          },
          endAdornment: { sx: { display: "flex", lineHeight: 0, marginInlineEnd: 0.5 } },
        }}
      />
    </Stack>
  );
};
